import { Timestamp } from '../lib/timestamp';
import { z } from 'zod';
import { OrganizationAddress, organizationAddressSchema } from '../types/address';

// Types
export type OrganizationType = 'club' | 'school';
export type OrganizationStatus = 'active' | 'inactive' | 'pending' | 'suspended';
export type Timezone = string; // e.g., 'America/New_York'
export type Currency = 'USD' | 'EUR' | 'GBP' | 'CAD' | 'AUD' | 'PLN' | 'CHF' | 'NZD';
export type RegulationPreference = 'FAA' | 'EASA' | 'auto';

export interface Coordinates {
  latitude: number;
  longitude: number;
}

export interface BookingPolicies {
  /** Maximum number of days ahead a member can book. 0 = unlimited. */
  maxDaysAhead?: number;
  /** Maximum number of active (non-completed, non-cancelled) bookings per member. 0 = unlimited. */
  maxActiveBookingsPerMember?: number;
  /** Minimum advance notice (hours) for non-admin bookings. 0 = no minimum. Default 1. */
  minAdvanceBookingHours?: number;
}

/** Default ENTRY unit for fuel in the completion modal; storage stays canonical litres. */
export type FuelUnit = 'liters' | 'us_gallon' | 'imp_gallon';
/** Default ENTRY unit for oil in the completion modal; storage stays canonical litres. */
export type OilUnit = 'liters' | 'us_quart';
/** Time basis an hours-based maintenance check counts down on (org default; overridable per check). */
export type MaintenanceBasis = 'tacho' | 'hobbs' | 'air' | 'block';

/**
 * Operational defaults & policy for the journey-log / flight-completion flow.
 * Defaults and policy only — per-flight values (times, uplift amounts) live on the booking.
 * See WebApp/docs/specs/journey-log-capture-fields.md §7. All keys optional for backward
 * compatibility; read through {@link getOperationsSettings} for fully-defaulted access.
 */
export interface OperationsSettings {
  /** Default entry units in the completion modal. Storage is always canonical litres. */
  units?: {
    fuel?: FuelUnit;
    oil?: OilUnit;
  };
  /** Which clock times the completion form shows (tacho/hobbs availability is per-aircraft). */
  timeCapture?: {
    trackBlock?: boolean;
    trackAirborne?: boolean;
  };
  /** Org default basis for hours-based checks; each check can override on its own definition. */
  maintenanceBasis?: MaintenanceBasis;
  journeyLog?: {
    /** Enforce block time / landings / route at completion (paper-alongside phase → off). */
    requireFields?: boolean;
    /** Prefill departure aerodrome from organizations.baseAirport.icaoCode. */
    defaultDepartureFromBase?: boolean;
  };
}

/** Sensible fallbacks applied when `settings.operations` (or any key within it) is absent. */
export const DEFAULT_OPERATIONS_SETTINGS: Required<{
  units: Required<NonNullable<OperationsSettings['units']>>;
  timeCapture: Required<NonNullable<OperationsSettings['timeCapture']>>;
  maintenanceBasis: MaintenanceBasis;
  journeyLog: Required<NonNullable<OperationsSettings['journeyLog']>>;
}> = {
  units: { fuel: 'liters', oil: 'liters' },
  timeCapture: { trackBlock: true, trackAirborne: true },
  maintenanceBasis: 'tacho',
  journeyLog: { requireFields: false, defaultDepartureFromBase: true },
};

export interface OrganizationSettings {
  timezone: Timezone;
  currency: Currency;
  regulationPreference: RegulationPreference;
  enableFlightSharing: boolean;
  enableDispatch: boolean;
  preflightRequirements: {
    requireWeatherCheck: boolean;
    requireWeightBalance: boolean;
    requireFlightPlan: boolean;
    requireMaintenanceCheck: boolean;
  };
  billingSettings?: {
    hourlyRate: number;
    minimumHours: number;
    requireDeposit: boolean;
    depositAmount?: number;
  };
  notificationSettings?: {
    emailNotifications: boolean;
    smsNotifications: boolean;
    notifyOnBooking: boolean;
    notifyOnMaintenance: boolean;
  };
  bookingVisibility?: {
    showMemberNames: boolean;
  };
  bookingPolicies?: BookingPolicies;
  /** DUE_SOON maintenance alert thresholds. Defaults: 10 hours, 30 days. */
  maintenanceAlertThresholds?: {
    hours: number;
    days: number;
  };
  /**
   * Operational defaults & policy for journey-log capture at flight completion.
   * Optional & backward compatible — read via {@link getOperationsSettings} for defaults.
   */
  operations?: OperationsSettings;
}

export interface Organization {
  id: string;
  name: string;
  slug: string;
  type: OrganizationType;
  status: OrganizationStatus;
  joinCode: string;
  
  // Base airport details
  baseAirport: {
    icaoCode: string;
    name: string;
    city: string;
    country: string;
    coordinates: Coordinates;
    address?: OrganizationAddress;
  };
  
  // Contact details
  contact: {
    email: string;
    phone?: string;
    website?: string;
    address?: OrganizationAddress;
  };
  
  // Metadata
  createdByUserId: string;
  ownerUid: string; // Same as createdByUserId
  createdAt: Timestamp;
  updatedAt: Timestamp;
  settings: OrganizationSettings;
  members: string[];
  
  // Optional fields
  logo?: string;
  description?: string;
  socialMedia?: {
    facebook?: string;
    twitter?: string;
    instagram?: string;
    linkedin?: string;
  };
}

// Validation schemas using Zod
export const coordinatesSchema = z.object({
  latitude: z.number().min(-90).max(90),
  longitude: z.number().min(-180).max(180),
});



export const organizationSettingsSchema = z.object({
  timezone: z.string(),
  currency: z.enum(['USD', 'EUR', 'GBP', 'CAD', 'AUD', 'PLN', 'CHF', 'NZD']),
  regulationPreference: z.enum(['FAA', 'EASA', 'auto']),
  enableFlightSharing: z.boolean(),
  enableDispatch: z.boolean(),
  preflightRequirements: z.object({
    requireWeatherCheck: z.boolean(),
    requireWeightBalance: z.boolean(),
    requireFlightPlan: z.boolean(),
    requireMaintenanceCheck: z.boolean(),
  }),
  billingSettings: z.object({
    hourlyRate: z.number().min(0),
    minimumHours: z.number().min(0),
    requireDeposit: z.boolean(),
    depositAmount: z.number().min(0).optional(),
  }).optional(),
  notificationSettings: z.object({
    emailNotifications: z.boolean(),
    smsNotifications: z.boolean(),
    notifyOnBooking: z.boolean(),
    notifyOnMaintenance: z.boolean(),
  }).optional(),
  bookingVisibility: z.object({
    showMemberNames: z.boolean(),
  }).optional(),
  bookingPolicies: z.object({
    maxDaysAhead: z.number().min(0).optional(),
    maxActiveBookingsPerMember: z.number().min(0).optional(),
    minAdvanceBookingHours: z.number().min(0).optional(),
  }).optional(),
  maintenanceAlertThresholds: z.object({
    hours: z.number().min(0),
    days: z.number().min(0),
  }).optional(),
  operations: z.object({
    units: z.object({
      fuel: z.optional(z.enum(['liters', 'us_gallon', 'imp_gallon'])),
      oil: z.optional(z.enum(['liters', 'us_quart'])),
    }).optional(),
    timeCapture: z.object({
      trackBlock: z.boolean().optional(),
      trackAirborne: z.boolean().optional(),
    }).optional(),
    maintenanceBasis: z.optional(z.enum(['tacho', 'hobbs', 'air', 'block'])),
    journeyLog: z.object({
      requireFields: z.boolean().optional(),
      defaultDepartureFromBase: z.boolean().optional(),
    }).optional(),
  }).optional(),
});

export const organizationSchema = z.object({
  id: z.string(),
  name: z.string().min(2).max(100),
  slug: z.string().min(2).max(100),
  type: z.enum(['club', 'school']),
  status: z.enum(['active', 'inactive', 'pending', 'suspended']),
  joinCode: z.string().length(8),
  baseAirport: z.object({
    icaoCode: z.string().length(4),
    name: z.string(),
    city: z.string(),
    country: z.string(),
    coordinates: coordinatesSchema,
    address: organizationAddressSchema.optional(),
  }),
  contact: z.object({
    email: z.string().email(),
    phone: z.string().optional(),
    website: z.string().url().optional(),
    address: organizationAddressSchema.optional(),
  }),
  createdByUserId: z.string(),
  ownerUid: z.string(),
  createdAt: z.custom<Timestamp>((val) => val instanceof Timestamp),
  updatedAt: z.custom<Timestamp>((val) => val instanceof Timestamp),
  settings: organizationSettingsSchema,
  members: z.array(z.string()),
  logo: z.string().url().optional(),
  description: z.string().max(1000).optional(),
  socialMedia: z.object({
    facebook: z.string().url().optional(),
    twitter: z.string().url().optional(),
    instagram: z.string().url().optional(),
    linkedin: z.string().url().optional(),
  }).optional(),
});

// Helper functions
export const createOrganization = (
  id: string,
  name: string,
  type: OrganizationType,
  baseAirport: Organization['baseAirport'],
  contact: Organization['contact'],
  createdByUserId: string,
  settings: OrganizationSettings,
  options?: {
    logo?: string;
    description?: string;
    socialMedia?: Organization['socialMedia'];
  }
): Organization => {
  const now = Timestamp.now();
  
  // Generate a random 8-character join code
  const joinCode = Math.random().toString(36).substring(2, 10).toUpperCase();
  
  const organization = {
    id,
    name,
    slug: generateSlug(name),
    type,
    status: 'pending' as OrganizationStatus,
    joinCode,
    baseAirport,
    contact,
    createdByUserId,
    ownerUid: createdByUserId,
    createdAt: now,
    updatedAt: now,
    settings,
    members: [createdByUserId],
    ...options
  };

  // Validate the organization
  organizationSchema.parse(organization);
  
  return organization;
};

export const generateSlug = (name: string): string => {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
};

export const validateCoordinates = (coordinates: Coordinates): boolean => {
  try {
    coordinatesSchema.parse(coordinates);
    return true;
  } catch {
    return false;
  }
};

export const validateOrganizationSettings = (settings: OrganizationSettings): boolean => {
  try {
    organizationSettingsSchema.parse(settings);
    return true;
  } catch {
    return false;
  }
};

export const isOrganizationActive = (organization: Organization): boolean => {
  return organization.status === 'active';
};

export const canAcceptBookings = (organization: Organization): boolean => {
  return isOrganizationActive(organization) && organization.settings.enableFlightSharing;
};

export const requiresPreflightChecks = (organization: Organization): boolean => {
  const { preflightRequirements } = organization.settings;
  return Object.values(preflightRequirements).some(requirement => requirement);
};

/**
 * Resolve the org's operations settings with every key defaulted, so callers (the
 * flight-completion UI) never have to null-check. Missing `settings.operations`, or any
 * key within it, falls back to {@link DEFAULT_OPERATIONS_SETTINGS}. Backward compatible
 * with legacy orgs that predate the operations block.
 */
export const getOperationsSettings = (
  organization: Organization
): typeof DEFAULT_OPERATIONS_SETTINGS => {
  const ops = organization.settings?.operations;
  const d = DEFAULT_OPERATIONS_SETTINGS;
  return {
    units: {
      fuel: ops?.units?.fuel ?? d.units.fuel,
      oil: ops?.units?.oil ?? d.units.oil,
    },
    timeCapture: {
      trackBlock: ops?.timeCapture?.trackBlock ?? d.timeCapture.trackBlock,
      trackAirborne: ops?.timeCapture?.trackAirborne ?? d.timeCapture.trackAirborne,
    },
    maintenanceBasis: ops?.maintenanceBasis ?? d.maintenanceBasis,
    journeyLog: {
      requireFields: ops?.journeyLog?.requireFields ?? d.journeyLog.requireFields,
      defaultDepartureFromBase:
        ops?.journeyLog?.defaultDepartureFromBase ?? d.journeyLog.defaultDepartureFromBase,
    },
  };
};

export const getOrganizationTimezone = (organization: Organization): string => {
  return organization.settings.timezone;
};

/** Default currency applied when an org predates the currency setting (settings.currency undefined). */
export const DEFAULT_CURRENCY: Currency = 'USD';

export const getOrganizationCurrency = (organization: Organization): Currency => {
  return organization.settings.currency || DEFAULT_CURRENCY;
};

export const updateOrganizationStatus = (
  organization: Organization,
  newStatus: OrganizationStatus
): Organization => {
  return {
    ...organization,
    status: newStatus,
    updatedAt: Timestamp.now(),
  };
};

export const updateOrganizationSettings = (
  organization: Organization,
  newSettings: Partial<OrganizationSettings>
): Organization => {
  const updatedSettings = {
    ...organization.settings,
    ...newSettings,
  };

  if (!validateOrganizationSettings(updatedSettings)) {
    throw new Error('Invalid organization settings');
  }

  return {
    ...organization,
    settings: updatedSettings,
    updatedAt: Timestamp.now(),
  };
}; 